import { supabase } from '../lib/supabase';
import { empresaService } from './empresaService';
import { modulosService } from './modulosService';
import { watchdogLog } from '../lib/watchdogLog';
import type { Tela } from '../models/tela.model';

export interface PareamentoHistorico {
  codigo: string;
  vinculado_em: string;
  revogado_em: string | null;
}

const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL      as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

export const telasService = {
  /** Retorna todas as telas; `null` = sem filtro (super_admin). */
  async listar(empresaId: string | null): Promise<Tela[]> {
    let query = supabase.from('telas').select('*').order('created_at', { ascending: true });
    if (empresaId !== null) query = query.eq('empresa_id', empresaId);
    const { data, error } = await query;
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Retorna a tela principal da empresa (padrão: 1 tela por empresa). Com 2+
   * telas, sempre a mais antiga — sem ORDER BY o Postgres devolvia uma
   * qualquer, e Controle Remoto, Configurações e Momento Especial podiam
   * apontar para TVs diferentes.
   */
  async buscarPorEmpresa(empresaId: string): Promise<Tela | null> {
    const { data, error } = await supabase
      .from('telas')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: true })
      .order('id', { ascending: true })
      .limit(1)
      .maybeSingle();
    // Erro de rede/RLS não pode parecer "empresa sem TV": o operador criaria outra tela.
    if (error) throw error;
    return (data as Tela | null) ?? null;
  },

  /** Busca uma tela pelo ID */
  async buscarPorId(id: string): Promise<Tela> {
    const { data, error } = await supabase
      .from('telas')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  /** Cria uma nova tela */
  async criar(tela: Omit<Tela, 'id' | 'created_at' | 'status' | 'ultima_conexao'>): Promise<Tela> {
    const { permitido, total, limite } = await empresaService.verificarLimiteTelas(tela.empresa_id);
    if (!permitido) {
      throw new Error(`Limite de telas atingido (${total}/${limite}).`);
    }

    const { data, error } = await supabase
      .from('telas')
      .insert({ ...tela, status: 'offline' } as never)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Atualiza ultima_conexao e marca status como 'online'.
   * Se houver eventos pendentes do Watchdog no localStorage, os envia para a
   * tabela `watchdog_logs` e limpa o buffer local somente após envio bem-sucedido.
   * Chamado pelo Player a cada 10 segundos (heartbeat).
   */
  async atualizarHeartbeat(
    id: string,
    contexto?: { telaNome?: string | null; empresaId?: string | null },
  ): Promise<void> {
    // fetch direto sem supabase client — elimina dependências de localStorage,
    // estado de auth e retry interno do cliente. Timeout de 8 s evita que uma
    // requisição travada bloqueie o próximo ciclo de 10 s.
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/atualizar_heartbeat_tela`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY },
        body:    JSON.stringify({ p_tela_id: id }),
        signal:  AbortSignal.timeout(8_000),
      });
      // fetch só rejeita em falha de rede: 401/500 da RPC precisam virar falha registrada.
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      console.warn('[Heartbeat] falha:', (e as Error).message);
    }

    const pendentes = watchdogLog.getAll();
    if (pendentes.length === 0) return;

    try {
      const { error } = await supabase.from('watchdog_logs').insert(
        pendentes.map(ev => ({
          tela_id:    ev.telaId,
          tela_nome:  contexto?.telaNome ?? null,
          empresa_id: contexto?.empresaId ?? null,
          tipo:       ev.type,
          acao:       ev.action,
          midia_id:   ev.midiaId,
          criado_em:  ev.ts,
        })) as never,
      );
      if (!error) watchdogLog.clear();
    } catch (e) {
      console.warn('[Watchdog] falha ao enviar log:', (e as Error).message);
    }
  },

  /** Vincula um código de pareamento (gerado pelo app da TV) a esta tela. */
  async vincularPareamento(codigo: string, telaId: string): Promise<void> {
    const { error } = await supabase.rpc('tenant_pareamento_vincular' as never, {
      p_codigo: codigo,
      p_tela_id: telaId,
    } as never);
    if (error) throw error;
  },

  /** Últimos pareamentos (TV Box) feitos nesta tela. */
  async listarPareamentos(telaId: string): Promise<PareamentoHistorico[]> {
    const { data, error } = await supabase.rpc('tenant_pareamento_historico' as never, {
      p_tela_id: telaId,
    } as never);
    if (error) throw error;
    return (data ?? []) as PareamentoHistorico[];
  },

  /** Desvincula uma TV já pareada: ela esquece a URL salva e volta pro código. */
  async desvincularPareamento(codigo: string): Promise<void> {
    const { error } = await supabase.rpc('tenant_pareamento_desvincular' as never, {
      p_codigo: codigo,
    } as never);
    if (error) throw error;
  },

  /** Vincula (ou desvincula, quando null) uma playlist a uma tela */
  async vincularPlaylist(telaId: string, playlistId: string | null): Promise<void> {
    if (playlistId) {
      const tela = await telasService.buscarPorId(telaId);

      // A policy telas_tenant só confere o empresa_id da tela e a FK não amarra a empresa da
      // playlist: sem esta checagem um tenant vincularia a playlist de outra empresa.
      const { data: playlist, error: playlistError } = await supabase
        .from('playlists')
        .select('id')
        .eq('id', playlistId)
        .eq('empresa_id', tela.empresa_id)
        .maybeSingle();
      if (playlistError) throw playlistError;
      if (!playlist) throw new Error('Playlist não pertence à empresa');

      // Regra de módulos (plano + exceção da empresa); o servidor também barra a fila.
      const modulos = await modulosService.meusModulos();
      if (!modulos.includes('jukebox')) {
        throw new Error('Jukebox não disponível no plano atual deste estabelecimento.');
      }
    }

    const { error } = await supabase
      .from('telas')
      .update({ playlist_id: playlistId } as never)
      .eq('id', telaId);

    if (error) throw error;
  },

  /** Atualiza campos editáveis da tela (nome, localização, configurações) */
  async atualizar(id: string, dados: Partial<Omit<Tela, 'id' | 'empresa_id' | 'created_at'>>): Promise<Tela> {
    const { data, error } = await supabase
      .from('telas')
      .update(dados as never)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /** Remove uma tela pelo ID */
  async excluir(id: string): Promise<void> {
    const { error } = await supabase
      .from('telas')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};
